import React, { useEffect, useState } from 'react';
import RialSymbol from '../../components/RialSymbol';
import { Box, Card, CardContent, Typography, Button, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Store as StoreIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrderIcon,
  TrendingUp as RevenueIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import merchantService from '../../services/merchant.service';
import LoadingAnimation from '../../components/LoadingAnimation';

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [business, setBusiness] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsData, businessData] = await Promise.all([
          merchantService.getStats(),
          merchantService.getBusiness(),
        ]);

        setStats(statsData);
        setBusiness(businessData);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <LoadingAnimation message="Loading merchant dashboard..." subtitle="Managing your business" />;
  }

  const completedOrders = stats?.totalOrders - stats?.pendingOrders - stats?.readyOrders || 0;
  const completionRate = stats?.totalOrders > 0 ? Math.round((completedOrders / stats.totalOrders) * 100) : 0;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Good day, {business?.name || 'Merchant'}! 🏪
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your surprise boxes and track your sales
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: '#0B6E4F', borderRadius: 2, p: 1.5, mr: 2 }}>
                <InventoryIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats?.totalBoxes || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Boxes
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: '#B1D65D', borderRadius: 2, p: 1.5, mr: 2 }}>
                <OrderIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats?.totalOrders || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: '#FF6B35', borderRadius: 2, p: 1.5, mr: 2 }}>
                <RevenueIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  <RialSymbol /> {stats?.totalRevenue?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: '#2196F3', borderRadius: 2, p: 1.5, mr: 2 }}>
                <StoreIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {completionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completion Rate
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/surprise-boxes')}
            >
              Create Surprise Box
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/orders')}
            >
              View Orders
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Performance Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Order Status Overview
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Pending Orders
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {stats?.pendingOrders || 0}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats?.totalOrders > 0 ? (stats.pendingOrders / stats.totalOrders) * 100 : 0}
              sx={{ height: 8, borderRadius: 1, bgcolor: '#FFE0B2', '& .MuiLinearProgress-bar': { bgcolor: '#FF6B35' } }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Ready for Pickup
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {stats?.readyOrders || 0}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats?.totalOrders > 0 ? (stats.readyOrders / stats.totalOrders) * 100 : 0}
              sx={{ height: 8, borderRadius: 1, bgcolor: '#E3F2FD', '& .MuiLinearProgress-bar': { bgcolor: '#2196F3' } }}
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Completed Orders
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {completedOrders}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{ height: 8, borderRadius: 1, bgcolor: '#E8F5E9', '& .MuiLinearProgress-bar': { bgcolor: '#0B6E4F' } }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
